import type {
  PositionAccount,
  PositionAdvisor,
  SormRow,
} from '../account-query-sorm-types'

import { readSormNumber, readSormString } from './sorm-row-readers'

function mapPositionAccount(row: SormRow): PositionAccount {
  return {
    id: readSormString(row.account_id),
    name: readSormString(row.account_name),
    principal_value: readSormNumber(row.principal_value),
    settled_principal_value: readSormNumber(row.settled_principal_value),
    market_value: readSormNumber(row.market_value),
    basis_market_value: readSormNumber(row.basis_market_value),
    gain_loss: readSormNumber(row.gain_loss),
    money_manager: null,
  }
}

/**
 * Groups SORM `Position` rows by advisor, in first-seen order, summing each
 * advisor's values. Rows are per account and sleeve, so an account holding the
 * instrument in several sleeves is summed into one entry.
 */
export function groupPositionsByAdvisor(rows: SormRow[]): PositionAdvisor[] {
  const advisorsById = new Map<string, PositionAdvisor>()
  const accountsByAdvisorId = new Map<string, Map<string, PositionAccount>>()
  for (const row of rows) {
    const advisorId = readSormString(row.advisor_id)
    const position = mapPositionAccount(row)
    let advisor = advisorsById.get(advisorId)
    let accounts = accountsByAdvisorId.get(advisorId)
    if (!advisor || !accounts) {
      advisor = {
        name: readSormString(row.advisor_name),
        num_accounts: 0,
        principal_value: 0,
        market_value: 0,
        basis_market_value: 0,
        gain_loss: 0,
        accounts: [],
      }
      accounts = new Map()
      advisorsById.set(advisorId, advisor)
      accountsByAdvisorId.set(advisorId, accounts)
    }
    const account = accounts.get(position.id)
    if (account) {
      account.principal_value += position.principal_value
      account.settled_principal_value += position.settled_principal_value
      account.market_value += position.market_value
      account.basis_market_value += position.basis_market_value
      account.gain_loss += position.gain_loss
    } else {
      accounts.set(position.id, position)
      advisor.accounts.push(position)
      advisor.num_accounts += 1
    }
    advisor.principal_value += position.principal_value
    advisor.market_value += position.market_value
    advisor.basis_market_value += position.basis_market_value
    advisor.gain_loss += position.gain_loss
  }
  return [...advisorsById.values()]
}
